import { Category } from '@modules/common/types';
import { mapEdgeCategoryToCategory } from '@modules/common/utils';
import {
  IndexMetadataViewFragment,
  Language,
  PredictionMetadataViewFragment,
  RecordLabelViewFragment,
  RecordType,
  RecordViewFragment,
  SentimentType,
} from 'generated/graphql';
import { dateObjFromUnixWithTZ } from 'v2/core/date/date';
import {
  Feedback,
  FeedbackLabel,
  FeedbackLabelType,
  FeedbackMatch,
  Label,
} from 'v2/core/entities/feedback';
import { SearchLabel } from 'v2/core/entities/feedback/labels';
import { LanguageFieldValue, SentimentName } from 'v2/core/entities/fields';
import { MatchedFeedbackMetadata } from 'v2/core/entities/labels';
import mapFeedbackContent, {
  mapRecordSummary,
} from 'v2/core/repository/edge-server/records/feedbackContentMapper';
import { MatchedFeedbackMapType } from 'v2/core/repository/edge-server/types';
import { mapSentimentNameString } from 'v2/core/repository/edge-server/utils/sentiment';
import { IsoLanguageMap } from 'v2/utils/language';

const feedbacksMapper = (
  records: RecordViewFragment[],
  timezone: string,
  matchedRecords?: MatchedFeedbackMapType,
): Feedback[] => {
  return records.map((record) =>
    feedbackMapper(record, timezone, getFeedbackMatch(matchedRecords, record)),
  );
};

const getSearchRecordMatches = (
  record: RecordViewFragment,
): MatchedFeedbackMetadata[] | undefined => {
  return record.matchMetadata?.search?.map((match) => ({
    type: 'SEARCH',
    startIDx: match.startIDx,
    endIDx: match.endIDx,
    msgIDx: match.msgIDx,
  }));
};

const getFeedbackMatch = (
  matchedRecords: MatchedFeedbackMapType | undefined,
  record: RecordViewFragment,
) => {
  const matches = matchedRecords?.matchData?.find(
    (md) => md.record.id === record.id,
  )?.matches;

  if (!matches) {
    return {
      type: matchedRecords?.type,
      matches: getSearchRecordMatches(record),
    };
  }

  return {
    type: matchedRecords?.type,
    matches,
  };
};

export const EnIsoLabel = 'eng';
const feedbackMapper = (
  record: RecordViewFragment,
  timezone: string,
  matchedRecord?: FeedbackMatch,
): Feedback => {
  if (!record.createdAt) {
    throw Error('record created at is not defined');
  }

  const searchLabel = getSearchLabel(matchedRecord);
  const isAudioFeedbackType = record.type === RecordType.AudioRecording;

  const otherLabels =
    mapLabels(record.predictions, {
      isAudioFeedbackType,
    }) ?? [];

  return {
    id: record.id,
    content: mapFeedbackContent(record),
    enterpretCreatedAt: dateObjFromUnixWithTZ(record.createdAt)!,
    sourceCreatedAt: dateObjFromUnixWithTZ(record.sourceTimestamp),
    language: mapLanguage(record.language),
    sourceV2: record.sourceDataV2,
    labels: searchLabel ? [searchLabel, ...otherLabels] : otherLabels,
    namespace: record.namespace,
    hasUnpredictedRCs: record.rcData?.units?.some(
      (rcUnit) => rcUnit.metadata?.isPredicted === false,
    ),
  };
};

const getSearchLabel = (
  matchedRecord?: FeedbackMatch,
): SearchLabel | undefined => {
  if (!matchedRecord || !matchedRecord.matches) {
    return;
  }

  return {
    id: 'static',
    displayName: 'SEARCH',
    type: FeedbackLabelType.Search,
    matches: matchedRecord.matches,
    isVisible: true,
  };
};

const mapLanguage = (lang?: Language): LanguageFieldValue => {
  const code = lang?.iso6393 ?? EnIsoLabel;
  return {
    format: 'iso6393',
    id: code,
    name: IsoLanguageMap[code],
  };
};

const mapCommonLabel = (
  label: RecordLabelViewFragment,
): Omit<Label, 'type'> => {
  return {
    id: label.id,
    createdAt: dateObjFromUnixWithTZ(label.createdAt),
    displayName: label.displayName,
    isVisible: label.isVisible,
  };
};

const mapLabels = (
  predictions: RecordViewFragment['predictions'],
  additionalParams: {
    isAudioFeedbackType: boolean;
  },
): Feedback['labels'] => {
  const { isAudioFeedbackType } = additionalParams;

  return predictions
    ?.filter(Boolean)
    .map((prediction): FeedbackLabel | undefined => {
      switch (prediction.label.__typename) {
        case 'Aspect':
          return mapAspectFeedbackLabel(
            prediction.label,
            prediction.predictionMetadata,
            isAudioFeedbackType,
          );
        case 'Cluster':
          return {
            ...mapCommonLabel(prediction.label),
            type: FeedbackLabelType.Cluster,
          };
        case 'Reason':
          return mapReasonFeedbackLabel(
            prediction.label,
            prediction.predictionMetadata,
          );
        case 'Sentiment':
          return {
            ...mapCommonLabel(prediction.label),
            type: FeedbackLabelType.Sentiment,
            displayName: mapSentimentNameString(prediction.label.displayName),
            sentimentUnits:
              prediction.predictionMetadata?.__typename ===
              'SentimentPredictionMetadata'
                ? prediction.predictionMetadata.units?.map((unit) => ({
                    ...mapPredictionIndexMetadata(unit.indexMetadata),
                    sentiment: mapSentimentName(unit.sentiment),
                  }))
                : undefined,
          };
        default:
          return undefined;
      }
    })
    ?.filter((label): label is FeedbackLabel => Boolean(label));
};

export const mapAspectFeedbackLabel = (
  label: RecordLabelViewFragment,
  predictionMetadata: PredictionMetadataViewFragment | undefined,
  isAudioFeedbackType: boolean,
): FeedbackLabel => {
  const categoriesSet: Set<Category> = new Set();

  return {
    ...mapCommonLabel(label),
    type: FeedbackLabelType.Entity,
    entityUnits:
      predictionMetadata?.__typename === 'AspectPredictionMetadata'
        ? predictionMetadata.units
            ?.filter((unit) => {
              // TODO: Remove this filter after contract update
              if (!isAudioFeedbackType) {
                return true;
              }
              const category =
                mapEdgeCategoryToCategory(unit?.category) ??
                Category.Uncategorized;

              const categoryAlreadyAdded = categoriesSet.has(category);
              categoriesSet.add(category);

              return !categoryAlreadyAdded;
            })
            .map((unit) => ({
              ...mapPredictionIndexMetadata(unit.indexMetadata),
              category: mapEdgeCategoryToCategory(unit.category),
              sentiment: mapSentimentName(unit.sentiment),
            }))
        : undefined,
    aggregatedSentiment:
      predictionMetadata?.__typename === 'AspectPredictionMetadata'
        ? mapSentimentName(predictionMetadata.sentiment)
        : undefined,
    keywordSummary:
      predictionMetadata?.__typename === 'AspectPredictionMetadata'
        ? mapRecordSummary(predictionMetadata.keywordSummary)
        : undefined,
    keywordSummaryHash:
      predictionMetadata?.__typename === 'AspectPredictionMetadata'
        ? predictionMetadata.keywordSummaryHash
        : undefined,
  };
};

export const mapReasonFeedbackLabel = (
  label: RecordLabelViewFragment,
  predictionMetadata: PredictionMetadataViewFragment | undefined,
): FeedbackLabel => {
  const sentiment =
    predictionMetadata?.__typename === 'ReasonPredictionMetadata'
      ? mapSentimentName(predictionMetadata.sentiment)
      : undefined;
  const category =
    label.__typename === 'Reason' ? label.metadata?.category : undefined;

  return {
    ...mapCommonLabel(label),
    type: FeedbackLabelType.Reason,
    sentiment,
    category: mapEdgeCategoryToCategory(category),
    reasonUnits:
      predictionMetadata?.__typename === 'ReasonPredictionMetadata'
        ? predictionMetadata.units?.map((unit) => ({
            ...mapPredictionIndexMetadata(unit.indexMetadata),
            sentiment,
          }))
        : undefined,
  };
};

export const mapSentimentName = (sentiment: SentimentType): SentimentName => {
  switch (sentiment) {
    case SentimentType.Negative:
      return SentimentName.NEGATIVE;
    case SentimentType.Neutral:
      return SentimentName.NEUTRAL;
    case SentimentType.Positive:
      return SentimentName.POSITIVE;
  }
};

export const mapPredictionIndexMetadata = (
  indexMetadata: IndexMetadataViewFragment,
): MatchedFeedbackMetadata => {
  switch (indexMetadata.__typename) {
    case 'IndexTypeText':
      return {
        type: 'TEXT',
        startIDx: indexMetadata.startOffset,
        endIDx: indexMetadata.endOffset,
      };
    case 'IndexTypeConversation':
      return {
        type: 'CONVERSATION',
        startIDx: indexMetadata.textIndex?.startOffset,
        endIDx: indexMetadata.textIndex?.endOffset,
        msgIDx: indexMetadata.messageIndex,
      };
    case 'IndexTypeSurvey':
      return {
        type: 'SURVEY',
        startIDx: indexMetadata.textIndex?.startOffset,
        endIDx: indexMetadata.textIndex?.endOffset,
        msgIDx: indexMetadata.qnaIndex,
        optionIDx: indexMetadata.optionIndex,
      };
    case 'IndexTypeKeywordSummary':
      return {
        type: 'KEYWORD_SUMMARY',
        keywordSummaryLabelID: indexMetadata.labelID,
        msgIDx: indexMetadata.msgIdx,
        startIDx: 0,
        endIDx: 0,
      };
    default:
      throw Error('Invalid IndexMetadata type');
  }
};

export default feedbacksMapper;
export { feedbackMapper };
